<template>
  <div class="row">
    <div class="col-md-12">
      <div class="category-top-bar">
        <div class="top-bar">
          <a class="btn btn-orange btn-icon back" :title="$t('general.back')" @click.prevent="goBack">
            <i class="ti-arrow-left"></i>
            {{ $t('general.back') }}
          </a>
        </div>
      </div>
      <p class="clearfix"></p>

      <!-- ================= LIST ================= -->
      <div v-show="!detail" class="card">
        <div class="card-content">
          <h4 class="subs-title">
            {{ $t('marketingSubscriptions.subscribersTitle') }}
            <small v-if="family"> — {{ family.name }}</small>
          </h4>

          <!-- State chips with counts -->
          <div class="subs-chips">
            <a v-for="chip in stateChips" :key="chip.value"
               class="subs-chip" :class="['subs-chip--' + (chip.value || 'all'), { 'is-active': filters.state === chip.value }]"
               @click="setState(chip.value)">
              {{ chip.label }} <span class="subs-chip__count">{{ counts[chip.value || 'all'] || 0 }}</span>
            </a>
          </div>

          <!-- Filters -->
          <div class="subs-filters">
            <div class="subs-filter subs-filter--search">
              <label class="subs-filter__label">{{ $t('marketingSubscriptions.customer') }}</label>
              <input type="text" class="form-control" v-model="filters.search" @input="onSearchInput" :placeholder="$t('marketingSubscriptions.searchSubscribers')">
            </div>
            <div class="subs-filter" v-if="family && family.levels.length > 1">
              <label class="subs-filter__label">{{ $t('marketingSubscriptions.level') }}</label>
              <el-select v-model="filters.subscription_id" clearable :placeholder="$t('marketingSubscriptions.allLevels')" @change="reload">
                <el-option v-for="level in family.levels" :key="level.id" :label="level.level + '. ' + level.name" :value="level.id"></el-option>
              </el-select>
            </div>
            <div class="subs-filter">
              <label class="subs-filter__label">{{ $t('marketingSubscriptions.autoRenew') }}</label>
              <el-select v-model="filters.auto_renew" @change="reload">
                <el-option :label="$t('marketingSubscriptions.stateAll')" value=""></el-option>
                <el-option :label="$t('marketingSubscriptions.yes')" value="1"></el-option>
                <el-option :label="$t('marketingSubscriptions.no')" value="0"></el-option>
              </el-select>
            </div>
            <div class="subs-filter subs-filter--dates">
              <label class="subs-filter__label">{{ $t('marketingSubscriptions.activeUntil') }}</label>
              <el-date-picker v-model="filters.active_until" type="daterange" value-format="yyyy-MM-dd" format="dd.MM.yyyy"
                              range-separator="-"
                              :start-placeholder="$t('marketingSubscriptions.dateFrom')"
                              :end-placeholder="$t('marketingSubscriptions.dateTo')"
                              @change="reload">
              </el-date-picker>
            </div>
          </div>

          <el-table :data="items" v-loading="loading" style="width: 100%" class="subs-table"
                    :empty-text="$t('marketingSubscriptions.noSubscribers')"
                    @row-click="openDetail" @sort-change="onSortChange"
                    :default-sort="{ prop: 'active_until', order: 'descending' }">
            <el-table-column :label="$t('marketingSubscriptions.customer')" min-width="200">
              <template slot-scope="props">
                <div v-if="props.row.user">
                  <strong>{{ props.row.user.name || ('#' + props.row.user.id) }}</strong>
                  <div class="subs-muted">{{ props.row.user.email }}<span v-if="props.row.user.phone"> · {{ props.row.user.phone }}</span></div>
                </div>
                <span v-else class="subs-muted">-</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('marketingSubscriptions.level')" min-width="130">
              <template slot-scope="props">
                <span v-if="props.row.subscription">{{ props.row.subscription.level }}. {{ props.row.subscription.name }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('marketingSubscriptions.status')" min-width="150">
              <template slot-scope="props">
                <span class="subs-state" :class="'subs-state--' + props.row.state">{{ stateLabel(props.row.state) }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="active_until" sortable="custom" :label="$t('marketingSubscriptions.activeUntil')" min-width="150">
              <template slot-scope="props">
                <span v-if="props.row.active_until">{{ props.row.active_until | formatDateTime }}</span>
                <span v-else class="subs-muted">-</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('marketingSubscriptions.autoRenew')" min-width="100">
              <template slot-scope="props">
                <span class="subs-renew" :class="props.row.auto_renew ? 'subs-renew--on' : 'subs-renew--off'" :title="renewTitle(props.row)">
                  <i class="fa" :class="props.row.auto_renew ? 'fa-refresh' : 'fa-ban'"></i>
                  {{ props.row.auto_renew ? $t('marketingSubscriptions.yes') : $t('marketingSubscriptions.no') }}
                </span>
              </template>
            </el-table-column>
            <el-table-column prop="total_paid" sortable="custom" :label="$t('marketingSubscriptions.totalPaid')" min-width="120">
              <template slot-scope="props">
                {{ props.row.total_paid }} {{ currency }}
              </template>
            </el-table-column>
            <el-table-column prop="last_payment_at" sortable="custom" :label="$t('marketingSubscriptions.lastPayment')" min-width="170">
              <template slot-scope="props">
                <template v-if="props.row.last_payment_at">
                  {{ props.row.last_payment_at | formatDateTime }}
                  <span v-if="props.row.last_payment_status !== null" class="subs-pay" :class="'subs-pay--' + props.row.last_payment_status">{{ paymentStatusLabel(props.row.last_payment_status) }}</span>
                </template>
                <span v-else class="subs-muted">-</span>
              </template>
            </el-table-column>
            <el-table-column width="110" align="center">
              <template slot-scope="props">
                <a class="subs-link" @click.stop="openDetail(props.row)">{{ $t('marketingSubscriptions.details') }} <i class="fa fa-angle-right"></i></a>
              </template>
            </el-table-column>
          </el-table>

          <el-pagination
            v-if="pagination.total > pagination.per_page"
            class="active-pagination subs-pagination"
            :total="pagination.total"
            :page-size="pagination.per_page"
            :current-page="pagination.page"
            layout="prev, pager, next, total"
            @current-change="handlePageChange">
          </el-pagination>
        </div>
      </div>

      <!-- ================= DETAIL ================= -->
      <div v-if="detail" class="card" v-loading="detailLoading">
        <div class="card-content">
          <a class="subs-link" @click="detail = null"><i class="fa fa-angle-left"></i> {{ $t('marketingSubscriptions.backToList') }}</a>

          <div class="subs-detail-head">
            <div>
              <h4 class="subs-title">{{ detail.user ? (detail.user.name || ('#' + detail.user.id)) : '-' }}</h4>
              <div class="subs-muted" v-if="detail.user">{{ detail.user.email }}<span v-if="detail.user.phone"> · {{ detail.user.phone }}</span></div>
            </div>
            <span class="subs-state subs-state--big" :class="'subs-state--' + detail.state">{{ stateLabel(detail.state) }}</span>
          </div>

          <div class="subs-facts">
            <div class="subs-fact">
              <div class="subs-fact__label">{{ $t('marketingSubscriptions.currentLevel') }}</div>
              <div class="subs-fact__value" v-if="detail.subscription">{{ detail.subscription.level }}. {{ detail.subscription.name }}</div>
            </div>
            <div class="subs-fact">
              <div class="subs-fact__label">{{ $t('marketingSubscriptions.activeUntil') }}</div>
              <div class="subs-fact__value">{{ detail.active_until ? $options.filters.formatDateTime(detail.active_until) : '-' }}</div>
            </div>
            <div class="subs-fact">
              <div class="subs-fact__label">{{ $t('marketingSubscriptions.renewalPrice') }}</div>
              <div class="subs-fact__value">{{ detail.price }} {{ currency }}</div>
            </div>
            <div class="subs-fact">
              <div class="subs-fact__label">{{ $t('marketingSubscriptions.totalPaid') }}</div>
              <div class="subs-fact__value">{{ detail.total_paid }} {{ currency }}</div>
            </div>
            <div class="subs-fact">
              <div class="subs-fact__label">{{ $t('marketingSubscriptions.autoRenew') }}</div>
              <div class="subs-fact__value">
                <span class="subs-renew" :class="detail.auto_renew ? 'subs-renew--on' : 'subs-renew--off'">
                  <i class="fa" :class="detail.auto_renew ? 'fa-refresh' : 'fa-ban'"></i>
                  {{ detail.auto_renew ? $t('marketingSubscriptions.yes') : $t('marketingSubscriptions.no') }}
                </span>
                <div v-if="!detail.auto_renew && detail.renewal_cancelled_at" class="subs-muted">
                  {{ $t('marketingSubscriptions.renewalCancelledAt') }}: {{ detail.renewal_cancelled_at | formatDateTime }}
                </div>
              </div>
            </div>
            <div class="subs-fact">
              <div class="subs-fact__label">{{ $t('marketingSubscriptions.card') }}</div>
              <div class="subs-fact__value">{{ detail.card_mask || '-' }}</div>
            </div>
            <div class="subs-fact">
              <div class="subs-fact__label">{{ $t('marketingSubscriptions.subscriberSince') }}</div>
              <div class="subs-fact__value">{{ detail.created_at ? $options.filters.formatDateTime(detail.created_at) : '-' }}</div>
            </div>
          </div>

          <!-- Operator actions on the subscription (no money moved); refunds are per payment below -->
          <div class="subs-actions" v-if="detail.can_cancel">
            <a v-if="detail.downgrade_levels && detail.downgrade_levels.length" class="btn btn-sm btn-warning" @click="openAction('downgrade')">
              <i class="fa fa-level-down"></i> {{ $t('marketingSubscriptions.downgrade') }}
            </a>
            <a class="btn btn-sm btn-danger" @click="openAction('cancel')">
              <i class="fa fa-ban"></i> {{ $t('marketingSubscriptions.cancelSubscription') }}
            </a>
          </div>
          <div v-if="action" class="subs-action-box" :class="'subs-action-box--' + action">
            <p class="subs-action-box__hint">
              {{ action === 'cancel' ? $t('marketingSubscriptions.cancelHint') : $t('marketingSubscriptions.downgradeHint') }}
            </p>
            <div v-if="action === 'downgrade'" class="form-group">
              <label>{{ $t('marketingSubscriptions.downgradeTo') }}*</label>
              <select class="form-control" v-model="actionLevelId">
                <option v-for="level in detail.downgrade_levels" :key="'dl' + level.id" :value="level.id">
                  {{ level.level }}. {{ level.name }} - {{ level.price }} {{ currency }}
                </option>
              </select>
            </div>
            <label>{{ $t('marketingSubscriptions.reason') }}*</label>
            <textarea class="form-control" rows="3" maxlength="1000" v-model="actionComment"></textarea>
            <small class="text-danger" v-if="actionError">{{ actionError }}</small>
            <div class="subs-refund__actions">
              <button class="btn btn-default btn-sm" :disabled="actionSaving" @click.prevent="action = null">{{ $t('general.cancel') }}</button>
              <button class="btn btn-sm btn-fill" :class="action === 'cancel' ? 'btn-danger' : 'btn-warning'" :disabled="actionSaving" @click.prevent="confirmAction">
                <i class="fa" :class="actionSaving ? 'fa-spinner fa-spin' : 'fa-check'"></i>
                {{ action === 'cancel' ? $t('marketingSubscriptions.cancelSubscription') : $t('marketingSubscriptions.downgrade') }}
              </button>
            </div>
          </div>

          <!-- History of the operator actions, with their reason -->
          <h5 class="subs-section">{{ $t('marketingSubscriptions.history') }}</h5>
          <p v-if="!detail.events || !detail.events.length" class="subs-muted">{{ $t('marketingSubscriptions.noHistory') }}</p>
          <div v-for="event in detail.events" :key="'ev' + event.id" class="subs-event" :class="'subs-event--' + event.type">
            <div class="subs-event__head">
              <strong>{{ eventLabel(event.type) }}</strong>
              <span class="subs-muted">{{ event.created_at | formatDateTime }}<span v-if="event.by"> · {{ event.by }}</span></span>
            </div>
            <div class="subs-event__details" v-if="eventDetails(event)">{{ eventDetails(event) }}</div>
            <div class="subs-event__comment">{{ event.comment }}</div>
          </div>

          <h5 class="subs-section">{{ $t('marketingSubscriptions.periods') }}</h5>
          <p v-if="!detail.periods || !detail.periods.length" class="subs-muted">{{ $t('marketingSubscriptions.noPeriods') }}</p>
          <div v-for="period in detail.periods" :key="'period' + period.id" class="subs-period">
            <div class="subs-period__head">
              <span class="subs-kind">{{ kindLabel(period.kind) }}</span>
              <strong v-if="period.subscription">{{ period.subscription.level }}. {{ period.subscription.name }}</strong>
              <span class="subs-muted">{{ period.period_start | formatDate }} → {{ period.period_end | formatDate }}</span>
              <span class="subs-period__amount">{{ period.amount }} {{ currency }}</span>
              <span class="subs-period-status" :class="'subs-period-status--' + period.status">{{ periodStatusLabel(period.status) }}</span>
            </div>
            <div class="subs-muted subs-period__meta" v-if="period.auto_charge_attempts || period.expiry_notified_at">
              <span v-if="period.auto_charge_attempts">{{ $t('marketingSubscriptions.autoChargeAttempts') }}: {{ period.auto_charge_attempts }}</span>
              <span v-if="period.expiry_notified_at"> · {{ $t('marketingSubscriptions.notifiedAt') }}: {{ period.expiry_notified_at | formatDateTime }}</span>
            </div>

            <div class="subs-payments">
              <div class="subs-payments__title">{{ $t('marketingSubscriptions.paymentAttempts') }}</div>
              <p v-if="!period.payments.length" class="subs-muted">{{ $t('marketingSubscriptions.noPayments') }}</p>
              <div v-for="payment in period.payments" :key="'payment' + payment.id" class="subs-payment">
                <div class="subs-payment__row">
                  <span class="subs-pay" :class="'subs-pay--' + payment.status">{{ paymentStatusLabel(payment.status) }}</span>
                  <span>{{ payment.method === 2 ? $t('marketingSubscriptions.methodSavedCard') : $t('marketingSubscriptions.methodLink') }}</span>
                  <span>{{ payment.amount }} {{ currency }}</span>
                  <span class="subs-muted">{{ payment.created_at | formatDateTime }}</span>
                  <span class="subs-muted" v-if="payment.reference">#{{ payment.reference }}</span>
                  <span class="subs-muted" v-if="payment.gateway_transaction_id">ep_id {{ payment.gateway_transaction_id }}</span>
                  <span class="subs-muted" v-if="payment.card_mask">{{ payment.card_mask }}</span>
                  <a v-if="payment.logs.length" class="subs-link subs-payment__logs-toggle" @click="toggleLogs(payment.id)">
                    {{ $t('marketingSubscriptions.showLogs') }} ({{ payment.logs.length }})
                    <i class="fa" :class="openLogs[payment.id] ? 'fa-angle-up' : 'fa-angle-down'"></i>
                  </a>
                </div>
                <div v-if="payment.error_message" class="subs-payment__error">{{ payment.error_message }}</div>
                <!-- refunded: type, when, who, why -->
                <div v-if="payment.status === 6" class="subs-refund-info">
                  <i class="fa fa-undo"></i>
                  {{ payment.refund_type === 'reversal' ? $t('marketingSubscriptions.refundTypeReversal') : $t('marketingSubscriptions.refundTypeRefund') }}
                  · {{ payment.refunded_at | formatDateTime }}
                  <span v-if="payment.refunded_by">· {{ payment.refunded_by }}</span>
                  <div class="subs-refund-info__comment">{{ payment.refund_comment }}</div>
                </div>
                <!-- payments of a period not ended yet: give the money back (the subscription is not changed) -->
                <div v-if="detail.refundable_payment_ids && detail.refundable_payment_ids.indexOf(payment.id) !== -1" class="subs-refund">
                  <a v-if="refundPaymentId !== payment.id" class="btn btn-sm btn-danger subs-refund__open" @click="openRefund(payment.id)">
                    <i class="fa fa-undo"></i> {{ $t('marketingSubscriptions.refundPayment') }}
                  </a>
                  <div v-else class="subs-refund__box">
                    <p class="subs-refund__hint">{{ $t('marketingSubscriptions.refundHint', { amount: payment.amount + ' ' + currency }) }}</p>
                    <label>{{ $t('marketingSubscriptions.reason') }}*</label>
                    <textarea class="form-control" rows="3" maxlength="1000" v-model="refundComment"></textarea>
                    <small class="text-danger" v-if="refundError">{{ refundError }}</small>
                    <div class="subs-refund__actions">
                      <button class="btn btn-default btn-sm" :disabled="refunding" @click.prevent="refundPaymentId = null">{{ $t('general.cancel') }}</button>
                      <button class="btn btn-danger btn-sm btn-fill" :disabled="refunding" @click.prevent="confirmRefund(payment)">
                        <i class="fa" :class="refunding ? 'fa-spinner fa-spin' : 'fa-undo'"></i> {{ $t('marketingSubscriptions.refundConfirm') }}
                      </button>
                    </div>
                  </div>
                </div>
                <div v-if="openLogs[payment.id]" class="subs-logs">
                  <div v-for="log in payment.logs" :key="'log' + log.id" class="subs-log">
                    <div class="subs-log__head">
                      <strong>{{ log.action }}</strong>
                      <span class="subs-muted">{{ log.created_at | formatDateTime }}</span>
                      <span class="subs-muted" v-if="log.http_code">HTTP {{ log.http_code }}</span>
                      <span class="subs-muted" v-if="log.duration_ms">{{ log.duration_ms }} ms</span>
                    </div>
                    <div class="subs-log__label">{{ $t('marketingSubscriptions.request') }}</div>
                    <pre class="subs-log__body">{{ prettyJson(log.request) }}</pre>
                    <div class="subs-log__label">{{ $t('marketingSubscriptions.response') }}</div>
                    <pre class="subs-log__body">{{ prettyJson(log.response) }}</pre>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
  import Vue from 'vue'
  import {Input, Select, Option, Table, TableColumn, Pagination, DatePicker} from 'element-ui'
  import swal from 'sweetalert2'
  import 'sweetalert2/dist/sweetalert2.css'

  Vue.use(Input)
  Vue.use(Select)
  Vue.use(Option)
  Vue.use(Table)
  Vue.use(TableColumn)
  Vue.use(Pagination)
  Vue.use(DatePicker)

  // Must match CustomerSubscription::STATES / Payment / CustomerSubscriptionPeriod constants in the API.
  const STATES = ['active', 'expiring', 'unpaid', 'expired', 'cancelled', 'pending', 'abandoned']
  const STATE_LABEL_KEYS = {
    active: 'stateActive',
    expiring: 'stateExpiring',
    unpaid: 'stateUnpaid',
    expired: 'stateExpired',
    cancelled: 'stateCancelled',
    pending: 'statePending',
    abandoned: 'stateAbandoned'
  }
  const PAYMENT_STATUS_KEYS = ['payCreated', 'payPending', 'paySuccess', 'payFailed', 'payExpired', 'payCancelled', 'payRefunded']
  const PERIOD_STATUS_KEYS = ['periodUnpaid', 'periodPaid', 'periodCancelled']
  const KIND_KEYS = { 1: 'kindFirst', 2: 'kindRenewal', 3: 'kindUpgrade' }
  const SORT_PROPS = ['active_until', 'total_paid', 'last_payment_at']
  const PER_PAGE = 20

  export default {
    props: {
      route: {
        type: Object,
        default: () => ({})
      }
    },
    data () {
      const query = this.route.query || {}
      return {
        loading: false,
        detailLoading: false,
        currency: '',
        family: null,
        items: [],
        counts: {},
        pagination: { total: 0, page: 1, per_page: PER_PAGE },
        filters: {
          state: '',
          search: '',
          subscription_id: query.subscription_id ? parseInt(query.subscription_id, 10) : '',
          auto_renew: '',
          active_until: null
        },
        sort: 'active_until',
        dir: 'desc',
        detail: null,
        openLogs: {},
        // refund + cancel (last successful payment)
        refundPaymentId: null,
        refundComment: '',
        refundError: '',
        refunding: false,
        // operator action on the subscription: 'cancel' | 'downgrade' | null
        action: null,
        actionLevelId: null,
        actionComment: '',
        actionError: '',
        actionSaving: false,
        searchTimer: null
      }
    },
    computed: {
      familyId () {
        return this.route.query && this.route.query.family_id ? parseInt(this.route.query.family_id, 10) : null
      },
      stateChips () {
        return [{ value: '', label: this.$t('marketingSubscriptions.stateAll') }]
          .concat(STATES.map(state => ({ value: state, label: this.stateLabel(state) })))
      }
    },
    created () {
      this.currency = this.getCurrencySymbol(this.$session.get('selectedCompanyData').currency_iso)
      this.getFamily()
      this.getSubscribers()
    },
    beforeDestroy () {
      clearTimeout(this.searchTimer)
    },
    methods: {
      requestData () {
        return {
          token: this.$session.get('token'),
          company_id: this.$session.get('selectedCompany')
        }
      },
      getFamily () {
        if (!this.familyId) {
          return
        }
        this.apiRequest('/subscription', 'GET', this.requestData(), (response) => {
          if (response.status === true) {
            this.family = (response.data || []).find(item => item.id === this.familyId) || null
          }
        })
      },
      getSubscribers () {
        const data = {
          ...this.requestData(),
          page: this.pagination.page,
          per_page: PER_PAGE,
          sort: this.sort,
          dir: this.dir
        }
        if (this.familyId) {
          data.family_id = this.familyId
        }
        if (this.filters.subscription_id) {
          data.subscription_id = this.filters.subscription_id
        }
        if (this.filters.state) {
          data.state = this.filters.state
        }
        if (this.filters.auto_renew !== '') {
          data.auto_renew = this.filters.auto_renew
        }
        if (this.filters.search.trim().length >= 2) {
          data.search = this.filters.search.trim()
        }
        if (this.filters.active_until && this.filters.active_until[0]) {
          data.active_until_from = this.filters.active_until[0]
          data.active_until_to = this.filters.active_until[1]
        }
        this.loading = true
        this.apiRequest('/subscription/subscribers', 'GET', data, (response) => {
          this.loading = false
          if (response.status === true && response.data) {
            this.items = response.data.items || []
            this.counts = response.data.counts || {}
            this.pagination = response.data.pagination || this.pagination
          } else {
            swal('', this.$t('validations.wentWrong'), 'error').catch(() => {})
          }
        })
      },
      reload () {
        this.pagination.page = 1
        this.getSubscribers()
      },
      setState (state) {
        this.filters.state = state
        this.reload()
      },
      onSearchInput () {
        clearTimeout(this.searchTimer)
        this.searchTimer = setTimeout(() => {
          const term = this.filters.search.trim()
          if (term.length >= 2 || term.length === 0) {
            this.reload()
          }
        }, 400)
      },
      onSortChange ({ prop, order }) {
        if (!order || SORT_PROPS.indexOf(prop) === -1) {
          this.sort = 'active_until'
          this.dir = 'desc'
        } else {
          this.sort = prop
          this.dir = order === 'ascending' ? 'asc' : 'desc'
        }
        this.reload()
      },
      handlePageChange (page) {
        this.pagination.page = page
        this.getSubscribers()
      },
      openDetail (row) {
        this.detail = { ...row, periods: [] }
        this.openLogs = {}
        this.refundPaymentId = null
        this.action = null
        this.detailLoading = true
        this.apiRequest('/subscription/subscribers/' + row.id, 'GET', this.requestData(), (response) => {
          this.detailLoading = false
          if (response.status === true && response.data) {
            this.detail = response.data
          } else {
            swal('', this.$t('validations.wentWrong'), 'error').catch(() => {})
          }
        })
      },
      openAction (type) {
        this.action = type
        this.actionComment = ''
        this.actionError = ''
        this.actionLevelId = type === 'downgrade' && this.detail.downgrade_levels.length ? this.detail.downgrade_levels[0].id : null
      },
      confirmAction () {
        const comment = (this.actionComment || '').trim()
        if (comment.length < 3) {
          this.actionError = this.$t('marketingSubscriptions.reasonRequired')
          return
        }
        const isCancel = this.action === 'cancel'
        const level = !isCancel ? this.detail.downgrade_levels.find(item => item.id === this.actionLevelId) : null
        if (!isCancel && !level) {
          this.actionError = this.$t('marketingSubscriptions.downgradeTo')
          return
        }
        this.actionError = ''
        swal({
          text: isCancel
            ? this.$t('marketingSubscriptions.cancelAreYouSure')
            : this.$t('marketingSubscriptions.downgradeAreYouSure', { name: level.name }),
          type: 'warning',
          showCancelButton: true
        }).then((result) => {
          if (!result) {
            return
          }
          const data = { ...this.requestData(), comment: comment }
          if (!isCancel) {
            data.subscription_id = level.id
          }
          this.actionSaving = true
          this.apiRequest('/subscription/subscribers/' + this.detail.id + '/' + (isCancel ? 'cancel' : 'downgrade'), 'POST', data, (response) => {
            this.actionSaving = false
            if (response.status === true && response.data) {
              this.detail = response.data
              this.action = null
              this.getSubscribers()
              swal('', this.$t(isCancel ? 'marketingSubscriptions.cancelDone' : 'marketingSubscriptions.downgradeDone'), 'success').catch(() => {})
              return
            }
            const errors = response.errors || {}
            const key = Object.keys(errors)[0]
            this.actionError = key ? (Array.isArray(errors[key]) ? errors[key][0] : errors[key]) : this.$t('validations.wentWrong')
          })
        }).catch(() => {})
      },
      eventLabel (type) {
        const keys = { cancel: 'eventCancel', downgrade: 'eventDowngrade', refund: 'eventRefund' }
        return keys[type] ? this.$t('marketingSubscriptions.' + keys[type]) : type
      },
      eventDetails (event) {
        const d = event.data || {}
        if (event.type === 'downgrade') {
          return d.from_name + ' (' + d.price_before + ' ' + this.currency + ') -> ' + d.to_name + ' (' + d.price_after + ' ' + this.currency + ')'
        }
        if (event.type === 'refund') {
          const type = d.refund_type === 'reversal' ? this.$t('marketingSubscriptions.refundTypeReversal') : this.$t('marketingSubscriptions.refundTypeRefund')
          return type + ': ' + d.amount + ' ' + (d.currency || this.currency) + (d.reference ? ' (#' + d.reference + ')' : '')
        }
        return ''
      },
      openRefund (paymentId) {
        this.refundPaymentId = paymentId
        this.refundComment = ''
        this.refundError = ''
      },
      confirmRefund (payment) {
        const comment = (this.refundComment || '').trim()
        if (comment.length < 3) {
          this.refundError = this.$t('marketingSubscriptions.refundCommentRequired')
          return
        }
        this.refundError = ''
        swal({
          text: this.$t('marketingSubscriptions.refundAreYouSure', { amount: payment.amount + ' ' + this.currency }),
          type: 'warning',
          showCancelButton: true
        }).then((result) => {
          if (!result) {
            return
          }
          const data = {
            ...this.requestData(),
            payment_id: payment.id,
            comment: comment
          }
          this.refunding = true
          this.apiRequest('/subscription/subscribers/' + this.detail.id + '/refund', 'POST', data, (response) => {
            this.refunding = false
            if (response.status === true && response.data) {
              this.detail = response.data
              this.refundPaymentId = null
              this.getSubscribers()
              swal('', this.$t('marketingSubscriptions.refundDone'), 'success').catch(() => {})
              return
            }
            const errors = response.errors || {}
            const key = Object.keys(errors)[0]
            this.refundError = key ? (Array.isArray(errors[key]) ? errors[key][0] : errors[key]) : this.$t('validations.wentWrong')
          })
        }).catch(() => {})
      },
      toggleLogs (paymentId) {
        this.$set(this.openLogs, paymentId, !this.openLogs[paymentId])
      },
      renewTitle (row) {
        if (row.auto_renew || !row.renewal_cancelled_at) {
          return ''
        }
        return this.$t('marketingSubscriptions.renewalCancelledAt') + ': ' + this.$options.filters.formatDateTime(row.renewal_cancelled_at)
      },
      stateLabel (state) {
        return STATE_LABEL_KEYS[state] ? this.$t('marketingSubscriptions.' + STATE_LABEL_KEYS[state]) : state
      },
      paymentStatusLabel (status) {
        const key = PAYMENT_STATUS_KEYS[status]
        return key ? this.$t('marketingSubscriptions.' + key) : status
      },
      periodStatusLabel (status) {
        const key = PERIOD_STATUS_KEYS[status]
        return key ? this.$t('marketingSubscriptions.' + key) : status
      },
      kindLabel (kind) {
        return KIND_KEYS[kind] ? this.$t('marketingSubscriptions.' + KIND_KEYS[kind]) : kind
      },
      prettyJson (value) {
        if (value === null || value === undefined || value === '') {
          return '-'
        }
        try {
          return JSON.stringify(JSON.parse(value), null, 2)
        } catch (e) {
          return value
        }
      },
      goBack () {
        this.$bus.$emit('refresh')
        this.$bus.$emit('close-modal')
      }
    }
  }
</script>
<style>
  .subs-title {
    margin: 0 0 12px;
  }
  .subs-muted {
    color: #999;
    font-size: 12px;
  }
  .subs-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 14px;
  }
  .subs-chip {
    cursor: pointer;
    padding: 5px 12px;
    border-radius: 16px;
    border: 1px solid #ddd;
    color: #555;
    font-size: 13px;
    background: #fff;
  }
  .subs-chip.is-active {
    border-color: #7e57c2;
    background: #ede7f6;
    color: #4527a0;
  }
  .subs-chip__count {
    display: inline-block;
    min-width: 20px;
    margin-left: 4px;
    padding: 0 6px;
    border-radius: 10px;
    background: rgba(0, 0, 0, 0.06);
    font-size: 12px;
    text-align: center;
  }
  .subs-filters {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 12px;
    margin-bottom: 14px;
  }
  .subs-filter {
    flex: 0 0 200px;
    max-width: 100%;
  }
  .subs-filter--search {
    flex-basis: 300px;
  }
  .subs-filter--dates {
    flex-basis: 300px;
  }
  .subs-filter__label {
    display: block;
    margin-bottom: 4px;
    font-size: 12px;
    color: #888;
    text-transform: none;
  }
  .subs-filters .el-select,
  .subs-filters .el-date-editor.el-range-editor,
  .subs-filters .form-control {
    width: 100% !important;
  }
  .subs-filters .form-control,
  .subs-filters .el-input__inner,
  .subs-filters .el-range-editor {
    height: 38px;
  }
  .subs-table .el-table__row {
    cursor: pointer;
  }
  .subs-link {
    cursor: pointer;
  }
  .subs-pagination {
    margin-top: 14px;
    text-align: right;
  }
  .subs-state {
    display: inline-block;
    padding: 2px 10px;
    border-radius: 10px;
    font-size: 12px;
    border: 1px solid transparent;
  }
  .subs-state--big {
    font-size: 14px;
    padding: 4px 14px;
  }
  .subs-state--active { background: #e8f5e9; color: #2e7d32; border-color: #c8e6c9; }
  .subs-state--expiring { background: #fff8e1; color: #8d6e00; border-color: #ffe082; }
  .subs-state--unpaid { background: #fdecea; color: #c62828; border-color: #f5c6c4; }
  .subs-state--expired { background: #f5f5f5; color: #757575; border-color: #e0e0e0; }
  .subs-state--cancelled { background: #f5f5f5; color: #757575; border-color: #e0e0e0; }
  .subs-state--pending { background: #e3f2fd; color: #1565c0; border-color: #bbdefb; }
  .subs-state--abandoned { background: #fafafa; color: #9e9e9e; border-color: #e0e0e0; border-style: dashed; }
  .subs-renew {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 10px;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 600;
    border: 1px solid transparent;
    white-space: nowrap;
  }
  .subs-renew--on { background: #e8f5e9; color: #2e7d32; border-color: #a5d6a7; }
  .subs-renew--off { background: #fdecea; color: #c62828; border-color: #f5c6c4; }
  .subs-pay {
    display: inline-block;
    margin-left: 6px;
    padding: 1px 8px;
    border-radius: 10px;
    font-size: 11px;
    background: #f5f5f5;
    color: #757575;
  }
  .subs-pay--1 { background: #e3f2fd; color: #1565c0; }
  .subs-pay--2 { background: #e8f5e9; color: #2e7d32; }
  .subs-pay--3 { background: #fdecea; color: #c62828; }
  .subs-pay--6 { background: #fff3e0; color: #e65100; }
  .subs-refund-info {
    margin-top: 6px;
    padding: 6px 10px;
    border-radius: 6px;
    background: #fff3e0;
    color: #e65100;
    font-size: 12px;
  }
  .subs-refund-info__comment {
    margin-top: 2px;
    color: #555;
    white-space: pre-wrap;
  }
  .subs-refund {
    margin-top: 6px;
  }
  .subs-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin: 0 0 10px;
  }
  .subs-action-box {
    max-width: 620px;
    padding: 10px 12px;
    margin-bottom: 14px;
    border-radius: 8px;
    border: 1px solid #f5c6c4;
    background: #fffafa;
  }
  .subs-action-box--downgrade {
    border-color: #ffe082;
    background: #fffdf5;
  }
  .subs-action-box__hint {
    font-size: 12px;
    color: #8d6e00;
    margin: 0 0 8px;
  }
  .subs-action-box--cancel .subs-action-box__hint {
    color: #c62828;
  }
  .subs-event {
    border-left: 3px solid #d1c4e9;
    padding: 4px 10px;
    margin-bottom: 8px;
  }
  .subs-event--cancel { border-left-color: #e57373; }
  .subs-event--downgrade { border-left-color: #ffd54f; }
  .subs-event--refund { border-left-color: #ffb74d; }
  .subs-event__head {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    font-size: 13px;
  }
  .subs-event__details {
    font-size: 12px;
    color: #555;
  }
  .subs-event__comment {
    font-size: 12px;
    color: #333;
    white-space: pre-wrap;
  }
  .subs-refund__box {
    max-width: 560px;
    padding: 10px 12px;
    border: 1px solid #f5c6c4;
    border-radius: 8px;
    background: #fffafa;
  }
  .subs-refund__hint {
    font-size: 12px;
    color: #c62828;
    margin: 0 0 8px;
  }
  .subs-refund__actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 8px;
  }
  .subs-detail-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 10px;
    margin: 14px 0;
  }
  .subs-facts {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
    gap: 10px;
    margin-bottom: 20px;
  }
  .subs-fact {
    background: #f8f8fb;
    border-radius: 8px;
    padding: 10px 12px;
  }
  .subs-fact__label {
    font-size: 12px;
    color: #888;
  }
  .subs-fact__value {
    font-size: 15px;
    font-weight: 600;
    margin-top: 2px;
  }
  .subs-section {
    margin: 10px 0;
    font-weight: 600;
  }
  .subs-period {
    border: 1px solid #eee;
    border-radius: 8px;
    padding: 10px 14px;
    margin-bottom: 10px;
  }
  .subs-period__head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
  }
  .subs-period__amount {
    margin-left: auto;
    font-weight: 600;
  }
  .subs-period__meta {
    margin-top: 4px;
  }
  .subs-kind {
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 11px;
    background: #ede7f6;
    color: #5e35b1;
  }
  .subs-period-status {
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 11px;
  }
  .subs-period-status--0 { background: #fdecea; color: #c62828; }
  .subs-period-status--1 { background: #e8f5e9; color: #2e7d32; }
  .subs-period-status--2 { background: #f5f5f5; color: #757575; }
  .subs-payments {
    margin-top: 8px;
    padding-left: 12px;
    border-left: 2px solid #f0f0f0;
  }
  .subs-payments__title {
    font-size: 12px;
    color: #888;
    margin-bottom: 4px;
  }
  .subs-payment {
    padding: 6px 0;
  }
  .subs-payment__row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    font-size: 13px;
  }
  .subs-payment__row .subs-pay {
    margin-left: 0;
  }
  .subs-payment__logs-toggle {
    margin-left: auto;
    font-size: 12px;
  }
  .subs-payment__error {
    color: #c62828;
    font-size: 12px;
    margin-top: 2px;
  }
  .subs-logs {
    margin-top: 6px;
  }
  .subs-log {
    background: #fafafa;
    border: 1px solid #eee;
    border-radius: 6px;
    padding: 8px 10px;
    margin-bottom: 6px;
  }
  .subs-log__head {
    display: flex;
    gap: 10px;
    font-size: 12px;
    margin-bottom: 4px;
  }
  .subs-log__label {
    font-size: 11px;
    color: #888;
    margin-top: 4px;
  }
  .subs-log__body {
    max-height: 220px;
    overflow: auto;
    font-size: 11px;
    margin: 2px 0 0;
    white-space: pre-wrap;
    word-break: break-all;
  }
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Marketing/Subscriptions/SubscribersPage.vue